/**
 * Нийлүүлэгчийн тооцоо (`/accounting/suppliers`, `/suppliers/:id/ledger`).
 *
 * ӨГЛӨГ — бид нийлүүлэгчид өртэй: зээлээр авсан гутал (₽) − төлсөн.
 * АВЛАГА — нийлүүлэгч бидэнд өртэй: «Нийлүүлэгч рүү буцаана» гэж буцаасан
 *          гутлын худалдан авсан ₽. Тооцоо хаах логик хараахан байхгүй —
 *          зөвхөн жагсаана.
 *
 * ⚠️ Өр ₽-ээр. ₮ нь цуглуулагчийн аяллын ханшаар (Монголбанк биш).
 *    Төлбөр бүртгэх нь «Зээл, төлөлт» хуудсанд.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatDate, formatMnt, formatRub, money } from '@boots/domain';
import { api, type Envelope } from '../../lib/api';
import { DataTable } from '../data-table';
import { Section } from '../section';
import { Button, Empty, ErrorNote, Modal, Spinner } from '../ui';
import { AccountingFilters, ExcelButton, saveTableXlsx, useAccountingFilter, type AccountingFilter } from './shared';

interface SupplierRow {
  supplier: { id: string; name: string };
  openingRub: string;
  takenRub: string;
  paidRub: string;
  closingRub: string;
  overdue: boolean;
  returnedToSupplier: { count: number; rub: string } | null;
}

interface LedgerLine {
  at: string;
  kind: 'credit' | 'payment';
  ref: string;
  note: string | null;
  debitRub: string;
  creditRub: string;
  balanceRub: string;
  amountMnt: string | null;
  paidMnt: string | null;
}
interface LedgerMeta { openingRub: string; takenRub: string; paidRub: string; closingRub: string }

const dash = (v: string) => (money(v).isZero() ? '—' : formatRub(v));

export function SuppliersTab() {
  const f = useAccountingFilter();
  const [open, setOpen] = useState<SupplierRow['supplier'] | null>(null);
  const q = useQuery({
    queryKey: ['accounting', 'suppliers', f.query()],
    queryFn: () => api<Envelope<SupplierRow[]>>(`/accounting/suppliers?${f.query()}`),
  });
  const rows = q.data?.data ?? [];

  const excel = () =>
    saveTableXlsx({
      file: `niiluulegch-tootsoo-${f.from}-${f.to}.xlsx`,
      title: 'Нийлүүлэгчийн тооцоо (₽)',
      period: `${f.from} — ${f.to}`,
      headers: ['Нийлүүлэгч', 'Эхний үлдэгдэл ₽', 'Зээлээр авсан ₽', 'Төлсөн ₽', 'Эцсийн үлдэгдэл ₽', 'Хугацаа хэтэрсэн', 'Буцаасан ширхэг', 'Буцаасан ₽ (авлага)'],
      widths: [24, 16, 16, 14, 16, 10, 10, 16],
      rows: rows.map((r) => [
        r.supplier.name, { money: r.openingRub }, { money: r.takenRub }, { money: r.paidRub }, { money: r.closingRub },
        r.overdue ? 'Тийм' : '', r.returnedToSupplier?.count ?? 0, { money: r.returnedToSupplier?.rub ?? '0' },
      ]),
    });

  return (
    <Section
      title="Нийлүүлэгчийн тооцоо"
      subtitle="өглөг ба авлага, ₽"
      note="Мөр дээр дарахад гүйлгээний дэвтэр нээгдэнэ. Төлбөрийг «Зээл, төлөлт» хуудсанд бүртгэнэ."
      actions={
        <div className="flex gap-2">
          <Link to="/credit"><Button variant="ghost">Зээл, төлөлт</Button></Link>
          <ExcelButton onClick={excel} disabled={rows.length === 0} />
        </div>
      }
    >
      <AccountingFilters f={f} store={false} />
      {q.error ? <ErrorNote>{(q.error as Error).message}</ErrorNote> : null}
      <DataTable
        rows={rows}
        loading={q.isPending}
        rowKey={(r) => r.supplier.id}
        onRowClick={(r) => setOpen(r.supplier)}
        empty={<Empty>Зээлтэй нийлүүлэгч алга</Empty>}
        columns={[
          {
            key: 'name', header: 'Нийлүүлэгч', mobile: 'title',
            cell: (r) => (
              <>
                {r.supplier.name}
                {r.overdue ? <span className="ml-2 text-label text-alert-ink">хугацаа хэтэрсэн</span> : null}
              </>
            ),
          },
          { key: 'opening', header: 'Эхний үлдэгдэл', align: 'right', nowrap: true, cell: (r) => dash(r.openingRub) },
          { key: 'taken', header: 'Зээлээр авсан', align: 'right', nowrap: true, cell: (r) => dash(r.takenRub) },
          { key: 'paid', header: 'Төлсөн', align: 'right', nowrap: true, cell: (r) => dash(r.paidRub) },
          { key: 'closing', header: 'Өглөг', align: 'right', nowrap: true, mobile: 'value', cell: (r) => <b>{dash(r.closingRub)}</b> },
          {
            key: 'rec', header: 'Авлага (буцаасан)', align: 'right', nowrap: true,
            cell: (r) => (r.returnedToSupplier ? `${r.returnedToSupplier.count} ш · ${formatRub(r.returnedToSupplier.rub)}` : '—'),
          },
        ]}
      />
      {open ? <LedgerModal supplier={open} f={f} onClose={() => setOpen(null)} /> : null}
    </Section>
  );
}

function LedgerModal({ supplier, f, onClose }: {
  supplier: { id: string; name: string };
  f: AccountingFilter;
  onClose: () => void;
}) {
  const query = new URLSearchParams({ from: f.from, to: f.to }).toString();
  const q = useQuery({
    queryKey: ['accounting', 'ledger', supplier.id, query],
    queryFn: () => api<Envelope<LedgerLine[]> & { meta: LedgerMeta }>(`/accounting/suppliers/${supplier.id}/ledger?${query}`),
  });
  const lines = q.data?.data ?? [];
  const meta = q.data?.meta;

  const excel = () =>
    saveTableXlsx({
      file: `devter-${supplier.name}-${f.from}-${f.to}.xlsx`,
      title: `Дэвтэр — ${supplier.name}`,
      period: `${f.from} — ${f.to} · эхний үлдэгдэл ${meta?.openingRub ?? '0'} ₽`,
      headers: ['Огноо', 'Гүйлгээ', 'Аялал', 'Авсан ₽', 'Төлсөн ₽', 'Үлдэгдэл ₽', 'Аяллын ханшаар ₮', 'Бодит төлсөн ₮', 'Тайлбар'],
      widths: [12, 12, 13, 14, 14, 14, 16, 16, 24],
      rows: lines.map((l) => [
        formatDate(l.at), l.kind === 'credit' ? 'Зээл' : 'Төлбөр', l.ref, { money: l.debitRub }, { money: l.creditRub },
        { money: l.balanceRub }, l.amountMnt ? { money: l.amountMnt } : null, l.paidMnt ? { money: l.paidMnt } : null, l.note ?? '',
      ]),
    });

  return (
    <Modal
      title={`Дэвтэр — ${supplier.name}`}
      subtitle={`${formatDate(f.from)} — ${formatDate(f.to)}`}
      onClose={onClose}
      wide
      footer={
        <>
          <ExcelButton onClick={excel} disabled={lines.length === 0} />
          <Button variant="ghost" onClick={onClose}>Хаах</Button>
        </>
      }
    >
      {q.isPending ? <Spinner /> : null}
      {q.error ? <ErrorNote>{(q.error as Error).message}</ErrorNote> : null}
      {meta ? (
        <dl className="grid grid-cols-2 gap-3 rounded-card border border-hair px-3.5 py-3 text-[13px] sm:grid-cols-4">
          {[
            ['Эхний үлдэгдэл', meta.openingRub],
            ['Зээлээр авсан', meta.takenRub],
            ['Төлсөн', meta.paidRub],
            ['Эцсийн үлдэгдэл', meta.closingRub],
          ].map(([label, v]) => (
            <div key={label}>
              <dt className="text-[11.5px] text-ink-3">{label}</dt>
              <dd className="num font-semibold">{formatRub(v)}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      <DataTable
        rows={lines}
        rowKey={(l, i) => `${l.at}|${i}`}
        empty={<Empty>Энэ хугацаанд гүйлгээ алга</Empty>}
        columns={[
          { key: 'at', header: 'Огноо', nowrap: true, mobile: 'title', cell: (l) => formatDate(l.at) },
          { key: 'kind', header: 'Гүйлгээ', cell: (l) => (l.kind === 'credit' ? 'Зээлээр авсан' : 'Төлбөр') },
          { key: 'ref', header: 'Аялал', nowrap: true, cell: (l) => <span className="num">{l.ref}</span> },
          { key: 'debit', header: 'Авсан', align: 'right', nowrap: true, cell: (l) => dash(l.debitRub) },
          { key: 'credit', header: 'Төлсөн', align: 'right', nowrap: true, cell: (l) => dash(l.creditRub) },
          { key: 'bal', header: 'Үлдэгдэл', align: 'right', nowrap: true, mobile: 'value', cell: (l) => <b>{formatRub(l.balanceRub)}</b> },
          {
            key: 'mnt', header: '₮', align: 'right', nowrap: true,
            cell: (l) => (l.paidMnt ? formatMnt(l.paidMnt) : l.amountMnt ? <span className="text-ink-3">{formatMnt(l.amountMnt)}</span> : '—'),
          },
        ]}
      />
    </Modal>
  );
}
